import { useMemo } from 'react';

import { DatePicker, Select, TextField } from '@atom/mui';
import { DataType } from '@atom/types/dataType';
import type { InventorySchemaItem } from '@atom/types/inventory';
import type {
  CalendarTrigger,
  DateFieldTrigger,
  NumericFieldTrigger,
  ScheduleCadence,
  ScheduleTrigger,
} from '@atom/types/workSchedule';

import classes from './schedule.module.css';

const { MenuItem } = Select;

type TriggerBuilderProps = {
  trigger: ScheduleTrigger;
  schema?: InventorySchemaItem;
  onChange: (next: ScheduleTrigger) => void;
};

type SourceKind = 'CALENDAR' | 'SCHEMA_FIELD';

const cadenceOptions: { value: ScheduleCadence; label: string }[] = [
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'ANNUAL', label: 'Annual' },
  { value: 'CUSTOM_DAYS', label: 'Every N days' },
];

const dateFieldOperators: { value: DateFieldTrigger['operator']; label: string }[] = [
  { value: 'DAYS_SINCE_GTE', label: 'Days since ≥' },
  { value: 'DAYS_UNTIL_LTE', label: 'Days until ≤' },
  { value: 'ON_OR_AFTER', label: 'On or after date' },
];

const numericFieldOperators: { value: NumericFieldTrigger['operator']; label: string }[] = [
  { value: '>=', label: '≥' },
  { value: '>', label: '>' },
  { value: '=', label: '=' },
  { value: '<=', label: '≤' },
  { value: '<', label: '<' },
];

const isoFromDate = (d: Date | null | undefined): string =>
  d ? d.toISOString().slice(0, 10) : '';

const dateFromIso = (iso: string | undefined): Date | undefined =>
  iso ? new Date(iso) : undefined;

const TriggerBuilder = (props: TriggerBuilderProps) => {
  const { trigger, schema, onChange } = props;

  const triggerableAttributes = useMemo(() => {
    if (!schema) return [];
    return Object.values(schema.attributes ?? {}).filter(
      (a: any) =>
        a.dataType === DataType.DATE ||
        a.dataType === DataType.DATE_TIME ||
        a.dataType === DataType.NUMBER ||
        a.dataType === DataType.CURRENCY,
    );
  }, [schema]);

  const sourceKind: SourceKind =
    trigger.kind === 'CALENDAR' ? 'CALENDAR' : 'SCHEMA_FIELD';

  const handleSourceChange = (next: SourceKind) => {
    if (next === 'CALENDAR') {
      onChange({
        kind: 'CALENDAR',
        cadence: 'MONTHLY',
        anchorDate: new Date().toISOString().slice(0, 10),
      });
    } else {
      const dateAttr = triggerableAttributes.find(
        (a: any) =>
          a.dataType === DataType.DATE || a.dataType === DataType.DATE_TIME,
      ) as any;
      const numericAttr = triggerableAttributes.find(
        (a: any) =>
          a.dataType === DataType.NUMBER || a.dataType === DataType.CURRENCY,
      ) as any;
      if (dateAttr) {
        onChange({
          kind: 'DATE_FIELD',
          attributeId: dateAttr.id ?? '',
          operator: 'DAYS_SINCE_GTE',
          thresholdDays: 30,
        });
      } else if (numericAttr) {
        onChange({
          kind: 'NUMERIC_FIELD',
          attributeId: numericAttr.id ?? '',
          operator: '>=',
          threshold: 0,
        });
      }
    }
  };

  const handleAttributeChange = (attributeId: string) => {
    const attr = triggerableAttributes.find(
      (a: any) => a.id === attributeId,
    ) as any;
    if (!attr) return;
    const isDate =
      attr.dataType === DataType.DATE || attr.dataType === DataType.DATE_TIME;
    if (isDate) {
      onChange({
        kind: 'DATE_FIELD',
        attributeId,
        operator: 'DAYS_SINCE_GTE',
        thresholdDays:
          trigger.kind === 'DATE_FIELD' ? trigger.thresholdDays ?? 30 : 30,
      });
    } else {
      onChange({
        kind: 'NUMERIC_FIELD',
        attributeId,
        mode:
          trigger.kind === 'NUMERIC_FIELD'
            ? trigger.mode ?? 'SINGLE'
            : 'SINGLE',
        operator: '>=',
        threshold:
          trigger.kind === 'NUMERIC_FIELD' ? trigger.threshold ?? 0 : 0,
        intervalAmount:
          trigger.kind === 'NUMERIC_FIELD'
            ? trigger.intervalAmount ?? 1000
            : 1000,
        intervalAnchor:
          trigger.kind === 'NUMERIC_FIELD' ? trigger.intervalAnchor ?? 0 : 0,
      });
    }
  };

  const handleNumericModeChange = (
    mode: NumericFieldTrigger['mode'],
  ) => {
    if (trigger.kind !== 'NUMERIC_FIELD') return;
    onChange({
      ...trigger,
      mode,
      // Seed sensible defaults for whichever mode the user just picked.
      ...(mode === 'INTERVAL'
        ? {
            intervalAmount: trigger.intervalAmount ?? 1000,
            intervalAnchor: trigger.intervalAnchor ?? 0,
          }
        : {
            operator: trigger.operator ?? '>=',
            threshold: trigger.threshold ?? 0,
          }),
    });
  };

  return (
    <div className={classes.triggerGrid}>
      <div className={classes.triggerField}>
        <Select
          label="Source"
          value={sourceKind}
          onChange={(e: any) => handleSourceChange(e.target.value as SourceKind)}
        >
          <MenuItem value="CALENDAR">Calendar</MenuItem>
          <MenuItem value="SCHEMA_FIELD">Schema Field</MenuItem>
        </Select>
      </div>

      {trigger.kind === 'CALENDAR' && (
        <>
          <div className={classes.triggerField}>
            <Select
              label="Cadence"
              value={trigger.cadence}
              onChange={(e: any) =>
                onChange({
                  ...trigger,
                  cadence: e.target.value as ScheduleCadence,
                } as CalendarTrigger)
              }
            >
              {cadenceOptions.map(o => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </div>
          {trigger.cadence === 'CUSTOM_DAYS' && (
            <div className={classes.triggerField}>
              <TextField
                label="Interval (days)"
                type="number"
                value={trigger.intervalDays ?? 30}
                onChange={(e: any) =>
                  onChange({
                    ...trigger,
                    intervalDays: Number(e.target.value),
                  } as CalendarTrigger)
                }
              />
            </div>
          )}
          <div className={classes.triggerField}>
            <DatePicker
              label="Anchor date"
              value={dateFromIso(trigger.anchorDate) as Date}
              onChange={(d: Date) =>
                onChange({
                  ...trigger,
                  anchorDate: isoFromDate(d),
                } as CalendarTrigger)
              }
            />
          </div>
        </>
      )}

      {trigger.kind !== 'CALENDAR' && (
        <>
          <div className={classes.triggerField}>
            <Select
              label="Field"
              value={(trigger as any).attributeId ?? ''}
              onChange={(e: any) => handleAttributeChange(e.target.value)}
            >
              {triggerableAttributes.map((a: any) => (
                <MenuItem key={a.id ?? ''} value={a.id ?? ''}>
                  {a.name}{a.isMeter ? ' (meter)' : ''}
                </MenuItem>
              ))}
            </Select>
          </div>
          {trigger.kind === 'DATE_FIELD' && (
            <>
              <div className={classes.triggerField}>
                <Select
                  label="Operator"
                  value={trigger.operator}
                  onChange={(e: any) =>
                    onChange({
                      ...trigger,
                      operator: e.target.value,
                    } as DateFieldTrigger)
                  }
                >
                  {dateFieldOperators.map(o => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </div>
              {trigger.operator === 'ON_OR_AFTER' ? (
                <div className={classes.triggerField}>
                  <DatePicker
                    label="Threshold date"
                    value={dateFromIso(trigger.thresholdDate) as Date}
                    onChange={(d: Date) =>
                      onChange({
                        ...trigger,
                        thresholdDate: isoFromDate(d),
                      } as DateFieldTrigger)
                    }
                  />
                </div>
              ) : (
                <div className={classes.triggerField}>
                  <TextField
                    label="Threshold (days)"
                    type="number"
                    value={trigger.thresholdDays ?? 0}
                    onChange={(e: any) =>
                      onChange({
                        ...trigger,
                        thresholdDays: Number(e.target.value),
                      } as DateFieldTrigger)
                    }
                  />
                </div>
              )}
            </>
          )}
          {trigger.kind === 'NUMERIC_FIELD' && (
            <>
              <div className={classes.triggerField}>
                <Select
                  label="Trigger"
                  value={trigger.mode ?? 'SINGLE'}
                  onChange={(e: any) =>
                    handleNumericModeChange(
                      e.target.value as NumericFieldTrigger['mode'],
                    )
                  }
                >
                  <MenuItem value="SINGLE">At value</MenuItem>
                  <MenuItem value="INTERVAL">Every</MenuItem>
                </Select>
              </div>
              {(trigger.mode ?? 'SINGLE') === 'SINGLE' ? (
                <>
                  <div className={classes.triggerField}>
                    <Select
                      label="Operator"
                      value={trigger.operator ?? '>='}
                      onChange={(e: any) =>
                        onChange({
                          ...trigger,
                          operator: e.target.value,
                        } as NumericFieldTrigger)
                      }
                    >
                      {numericFieldOperators.map(o => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                  <div className={classes.triggerField}>
                    <TextField
                      label="Threshold"
                      type="number"
                      value={trigger.threshold ?? 0}
                      onChange={(e: any) =>
                        onChange({
                          ...trigger,
                          threshold: Number(e.target.value),
                        } as NumericFieldTrigger)
                      }
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className={classes.triggerField}>
                    <TextField
                      label="Interval"
                      type="number"
                      value={trigger.intervalAmount ?? 0}
                      onChange={(e: any) =>
                        onChange({
                          ...trigger,
                          intervalAmount: Math.max(
                            0,
                            Number(e.target.value) || 0,
                          ),
                        } as NumericFieldTrigger)
                      }
                    />
                  </div>
                  <div className={classes.triggerField}>
                    <TextField
                      label="Starting at"
                      type="number"
                      value={trigger.intervalAnchor ?? 0}
                      onChange={(e: any) =>
                        onChange({
                          ...trigger,
                          intervalAnchor: Number(e.target.value) || 0,
                        } as NumericFieldTrigger)
                      }
                    />
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default TriggerBuilder;
